import React from 'react';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import { usePrivilege } from '../../components/priviledge/PriviledgeProvider';
import PageHeader from '../../layout/shared/page-header';
import ClientsSpending from './components/ClientsSpending';
import ContractsByStatus from './components/ContractsByStatuses';
import ContractsInfo from './components/ContractsInfo';
import FacilitatorDashboard from './components/FacilitatorDashboard';
import FacilitatorManagerDashboard from './components/FacilitatorManagerDashboard';
import OrdersList from './components/OrderCard';
import UsersContractsRanking from './components/UsersContracts';
import DashboardFilters from './DashboardFilters';
import { DashboardProvider } from './providers/DashboardProvider';
import {
    canAccessFacilitatorDashboard,
    canAccessFacilitatorManagerDashboard,
} from '../../utils/dashboardAccess';

const Dashboard: React.FC = () => {
    const { userCan, isFacilitator, isFacilitatorManager } = usePrivilege();
    const canShowCompleteDashboard = userCan('show_complete_dashboard', 'dashboard');
    const canShowFacilitatorManagerDashboard = canAccessFacilitatorManagerDashboard(userCan, { isFacilitatorManager })
        && userCan('show_all_system_facilitators', 'user');
    const canShowFacilitatorDashboard = !canShowFacilitatorManagerDashboard
        && canAccessFacilitatorDashboard(userCan, { isFacilitator, isFacilitatorManager });

    if (canShowFacilitatorManagerDashboard) {
        return (
            <div className='px-4'>
                <PageHeader title="Panel de control" />
                <div className="@container/crm px-2 pb-10">
                    <FacilitatorManagerDashboard />
                </div>
            </div>
        );
    }

    if (canShowFacilitatorDashboard) {
        return (
            <div className='px-4'>
                <PageHeader title="Panel de control" />
                <div className="@container/crm px-2 pb-10">
                    <FacilitatorDashboard />
                </div>
            </div>
        );
    }

    return (
        <DashboardProvider>
            <div className='px-4'>
                <PageHeader title="Panel de control">
                    <DashboardFilters />
                </PageHeader>
                <div className="@container/crm px-2 pb-10">
                    <div className="mb-6 flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-4 text-sm text-gray-700">
                        <HiOutlineInformationCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
                        <div>
                            <p className="font-medium text-gray-900">Bienvenido al dashboard</p>
                            <p>
                                {canShowCompleteDashboard
                                    ? 'Consulta el estado general de tu operativa, revisa los contadores clave y accede al listado de contratos con los filtros aplicados desde cada bloque.'
                                    : 'Consulta la distribución de contratos por estado según la configuración visible para tu área y entra al listado filtrado desde cada contador.'}
                            </p>
                        </div>
                    </div>

                    {canShowCompleteDashboard ? (
                        <div className="grid grid-cols-1 gap-6 @3xl/crm:grid-cols-12 @7xl/crm:gap-7">
                            <div className="@3xl/crm:col-span-full @7xl/crm:col-span-8 dark:bg-[#181818]">
                                <ContractsInfo />
                                <ClientsSpending className='mt-7' />
                                <OrdersList className='mt-7' />
                            </div>
                            <div className="@3xl/crm:col-span-6 @7xl/crm:col-span-4 dark:bg-[#181818]" >
                            <ContractsByStatus />
                                <UsersContractsRanking className='mt-7' />
                            </div>
                        </div>
                    ) : (
                        <div className="min-h-[calc(100vh-240px)]">
                            <ContractsByStatus variant="spotlight" />
                        </div>
                    )}
                </div>
            </div>
        </DashboardProvider>
    );
};

export default Dashboard;
